import React, { Fragment, ReactElement } from 'react';
import { useHistory, withRouter } from 'react-router';
import useStyles from './styles';
import GridLayout from '../../components/molecules/grid_layout';
import { useDispatch } from 'react-redux';
import { StateStatus } from '../../redux/utils/common';
import { Box, Button, IconButton, Paper, Typography } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import clsx from 'clsx';
import TranslatedText from '../../components/atoms/translated_text';
import {
  allowedActions,
  initialDefaultValues,
  Translations,
} from './configuration';
import * as AuthSelectors from '../../redux/reducers/gl_auth_reducer/selectors';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import { FormFields } from './configuration';
import BaseModal from '../../components/molecules/base_modal';
import { ModalIDs } from '../../redux/reducers/gl_modal_reducer/types';
import NavigationConfig from '../../navigation/config';
import IdentificationDetailsForm from './components/identification_details';
import VatReturnPeriodDetails from './components/vat_return_period_details';
import * as VatReturnDetailSelectors from '../../redux/reducers/vatReturn_detail_reducer/selectors';
import VatReturnDetailActions from '../../redux/reducers/vatReturn_detail_reducer/actions';
import handleIdentificationDetailsDefaultValues from './components/identification_details/formDefaultValues';
import handleVatReturnPeriodDetailsDefaultValues from './components/vat_return_period_details/formDefaultValues';
import SuppliesFromMemberStateOfIdentification from './components/supplies_from_member_state';
import AddSupplyFromMemberStateOfIdentificationForm from './components/supplies_from_member_state/components/add_supply_form';
import SuppliesFromFixedEstablishments from './components/supplies_from_fixed_establishments';
import AddSupplyFromFixedEstablishmentsForm from './components/supplies_from_fixed_establishments/components/add_supply_form';
import VatAmountTotal from './components/vat_amount_total';
import { getState } from 'redux/store';
import { ReducerKeys } from 'redux/reducers';
import * as DateUtils from 'utils/dates';
import CorrectionsOfVatReturn from './components/corrections_vat_return';
import AddVatReturnCorrectionForm from './components/corrections_vat_return/components/add_correction_form';
import BalanceVatDueForEachMemberState from './components/balance_vat_due_member_state';
import VatReturnTotalAmountDue from './components/vat_return_total_amount_due';
import { SchemeTypes } from 'config';

interface FetchCountriesWithVatRateInput {
  start_date?: string;
  end_date?: string;
  year?: number;
  quarter_id?: number;
  type: 'QUARTER' | 'DATE';
  excludeCyprus: boolean;
}

interface IProps {}
interface IElementsProps {
  size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  element: ReactElement;
  id: string;
  rowStyle?: any;
}

const VatReturnDetailPage: React.FC<IProps> = (props) => {
  // console.log('Render VatReturnDetailPage');
  const [submitError, setSubmitError] = React.useState<Array<string>>([]);
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const { detail, id } = (props as any).match.params;
  const mode: 'VIEW' | 'EDIT' = detail.toUpperCase();

  const ROLE = AuthSelectors.useSelectUserRole();
  const vatReturnDetail = VatReturnDetailSelectors.useGetVatReturnDetail();
  const { errors, control, handleSubmit, reset } = useForm<FormFields>({
    defaultValues: initialDefaultValues,
  });

  const state = getState(ReducerKeys.VATRETURN_LIST_REDUCER).vatReturnList.data;
  // console.log('---------->', state);
  let queryString = state ? `?goBack=true${state.queryString}` : '?goBack=true';

  const temporaryStatus =
    VatReturnDetailSelectors.useGetVatReturnTemporarySubmittionStatus();
  const finalStatus =
    VatReturnDetailSelectors.useGetVatReturnFinalSubmittionStatus();
  const nilStatus =
    VatReturnDetailSelectors.useGetVatReturnNilSubmittionStatus();

  React.useEffect(() => {
    return () => {
      dispatch(VatReturnDetailActions.resetReducerAction());
    };
  }, []);

  // FETCH VAT DETAIL
  React.useEffect(() => {
    dispatch(VatReturnDetailActions.fetchVatReturnDetailAction({ id: id }));
  }, [dispatch]);

  // SET FORM INITIAL VALUES
  React.useEffect(() => {
    if (vatReturnDetail && vatReturnDetail.status === StateStatus.Success) {
      reset({
        ...handleIdentificationDetailsDefaultValues(
          vatReturnDetail.data.identification_details
        ),
        ...handleVatReturnPeriodDetailsDefaultValues(
          vatReturnDetail.data.vat_return_period_details
        ),
      });
    }
  }, [vatReturnDetail]);

  React.useEffect(() => {
    let countries = getState(ReducerKeys.VATRETURN_DETAIL_REDUCER).countries;
    if (
      vatReturnDetail.status === StateStatus.Success &&
      vatReturnDetail.data &&
      !countries.data
    ) {
      let vat_return_period_details =
        vatReturnDetail.data.vat_return_period_details;
      let payload: FetchCountriesWithVatRateInput = {
        type: vat_return_period_details.start_date ? 'DATE' : 'QUARTER',
        ...(vat_return_period_details.start_date && {
          start_date: DateUtils.DateInQueryFormat(
            new Date(vat_return_period_details.start_date)
          ),
        }),
        ...(vat_return_period_details.end_date && {
          end_date: DateUtils.DateInQueryFormat(
            new Date(vat_return_period_details.end_date)
          ),
        }),
        year: vat_return_period_details.year,
        quarter_id: vat_return_period_details.vat_return_period_id,
        //excludeCyprus: vatReturnDetail.data.oss_scheme === 6 ? false : true,
        excludeCyprus: vatReturnDetail.data.oss_scheme === 4 ? true : false, //Exclude Cyprus when scheme is 4 (Union)
      };
      dispatch(VatReturnDetailActions.FetchCountriesWithVatRateAction(payload));
    }
  }, [dispatch, vatReturnDetail]);

  // React.useEffect(() => {
  //   if (errors && Object.keys(errors).length > 0) {
  //     showToastAction(
  //       dispatch,
  //       'There are some errors in the form. Please review the form.',
  //       'warning'
  //     );
  //   }
  // }, [errors]);

  const onSubmit = (data: any, event: any) => {
    const errors: Array<string> = [];
    let suppliesFromMemberStateOfIdentification = getState(
      ReducerKeys.VATRETURN_DETAIL_REDUCER
    ).vatReturnDetail.suppliesFromMemberStateOfIdentification;
    let suppliesFromFixedEstablishments = getState(
      ReducerKeys.VATRETURN_DETAIL_REDUCER
    ).vatReturnDetail.suppliesFromFixedEstablishments;
    let vatCorrections = getState(ReducerKeys.VATRETURN_DETAIL_REDUCER)
      .vatReturnDetail.vatCorrections;

    // console.log('data in temporary save and submit::', data);

    let payload = {
      supplies_from_member_state_of_identification:
        suppliesFromMemberStateOfIdentification.items,
      supplies_from_fixed_establishments: suppliesFromFixedEstablishments.items,
      vat_corrections: vatCorrections,
    };
    // console.log('payload in temporary save and submit::', payload);

    if (event.nativeEvent.submitter.id === 'final') {
      if (
        payload.supplies_from_fixed_establishments.length === 0 &&
        payload.supplies_from_member_state_of_identification.length === 0 &&
        payload.vat_corrections.length === 0
      ) {
        errors.push(
          'You have not declared any supplies, if you want to submit a nil Return please use the submit nil Return button'
        );
        setSubmitError(errors);
        return;
      }
      dispatch(
        VatReturnDetailActions.finalSaveVatReturnAction({
          vat_return_id: id,
          payload: payload,
          history: history,
        })
      );
    } else if (event.nativeEvent.submitter.id === 'temporary') {
      dispatch(
        VatReturnDetailActions.temporarySaveVatReturnAction({
          vat_return_id: id,
          payload: payload,
        })
      );
    } else if (event.nativeEvent.submitter.id === 'nil') {
      if (
        payload.supplies_from_fixed_establishments.length > 0 ||
        payload.supplies_from_member_state_of_identification.length > 0
      ) {
        errors.push(
          'You cannot Submit Nil Return with declared supplies and VAT Amounts'
        );
        setSubmitError(errors);
        return;
      }
      let nilPayload = {
        supplies_from_member_state_of_identification: [],
        supplies_from_fixed_establishments: [],
        vat_corrections: [],
      };
      dispatch(
        VatReturnDetailActions.nilSaveVatReturnAction({
          vat_return_id: id,
          payload: nilPayload,
          history: history,
        })
      );
    }
    setSubmitError(errors);
  };

  function handlePageViews(schemeType: SchemeTypes): IElementsProps[] {
    return [
      {
        element: renderIdentificationDetailsForm(
          classes,
          mode,
          errors,
          control
        ),
        size: 12,
        id: 'identificationDetails',
      },
      {
        element: renderVatReturnPeriodDetails(classes, mode, errors, control),
        size: 12,
        id: 'vatReturnPeriodDetails',
      },
      {
        element: renderSuppliesFromMemberStateOfIdentification(
          classes,
          mode,
          vatReturnDetail.data.identification_details.vat_id_num,
          // countries,
          vatReturnDetail.suppliesFromMemberStateOfIdentification
        ),
        size: 12,
        id: 'suppliesFromMemberStateOfIdentification',
      },
      ...(schemeType !== SchemeTypes.NON_EU
        ? [
            {
              element: renderSuppliesFromFixedEstablishments(
                classes,
                mode,
                vatReturnDetail.data.identification_details.vat_id_num,
                // countries,
                id,
                vatReturnDetail.suppliesFromFixedEstablishments
              ),
              size: 12,
              id: 'suppliesFromFixedEstablishments',
            } as any,
          ]
        : []),
      {
        element: renderVatAmountTotal(
          classes,
          vatReturnDetail.suppliesFromMemberStateOfIdentification
            .total_vat_amount_payable_goods +
            vatReturnDetail.suppliesFromMemberStateOfIdentification
              .total_vat_amount_payable_services +
            vatReturnDetail.suppliesFromFixedEstablishments
              .total_vat_amount_payable_goods +
            vatReturnDetail.suppliesFromFixedEstablishments
              .total_vat_amount_payable_services
        ),
        size: 12,
        id: 'vatAmountTotal',
      },
      {
        element: renderCorrectionsOfVatReturn(classes, mode, id, schemeType),
        size: 12,
        id: 'correctionsOfVatReturn',
      },
      {
        element: renderBalanceVatDueForEachMemberState(
          classes,
          vatReturnDetail.mscon
        ),
        size: 12,
        id: 'balanceVatDueForEachMemberState',
      },
      {
        element: renderVatReturnTotalAmountDue(classes, vatReturnDetail.mscon),
        size: 12,
        id: 'vatReturnTotalAmountDue',
      },
    ];
  }

  return (
    <Fragment>
      <Paper
        className={clsx([
          classes.cardLayout,
          classes.titleLayout,
          classes.sticky_bar,
        ])}
        elevation={0}
        variant={'outlined'}
        square
      >
        <div className={classes.header}>
          <Box display="flex" alignItems={'center'}>
            <IconButton
              onClick={() =>
                history.push(
                  NavigationConfig.vatReturnsPage().path + queryString
                )
              }
              aria-label="delete"
              className={classes.back_button}
            >
              <ArrowBackIcon />
            </IconButton>
            <EditIcon style={{ marginRight: '.75rem' }} />
            <Typography component={'p'} variant={'h6'}>
              <TranslatedText
                textMap={Translations.page_title}
                defaultText={Translations.page_title.English}
              />
            </Typography>
          </Box>
          {vatReturnDetail && vatReturnDetail.status === StateStatus.Success && (
            <Box display="flex" alignItems={'center'}>
              {mode === 'EDIT' &&
                allowedActions.temporary.roles.includes(ROLE) && (
                  <Button
                    id="temporary"
                    type={'submit'}
                    form={'vatReturnForm'}
                    disabled={
                      !allowedActions.temporary.state_status.includes(
                        `${vatReturnDetail.data.vat_return_status_id}${vatReturnDetail.data.vat_return_state}`
                      ) ||
                      [temporaryStatus, finalStatus, nilStatus].includes(
                        StateStatus.Pending
                      )
                    }
                    className={classes.header_actions}
                  >
                    <TranslatedText
                      textMap={Translations.action_temporary_save}
                    />
                  </Button>
                )}
              {mode === 'EDIT' && allowedActions.final.roles.includes(ROLE) && (
                <Button
                  id="final"
                  type={'submit'}
                  form={'vatReturnForm'}
                  disabled={
                    !allowedActions.final.state_status.includes(
                      `${vatReturnDetail.data.vat_return_status_id}${vatReturnDetail.data.vat_return_state}`
                    ) ||
                    [temporaryStatus, finalStatus, nilStatus].includes(
                      StateStatus.Pending
                    )
                  }
                  className={classes.header_actions}
                >
                  <TranslatedText textMap={Translations.action_final_submit} />
                </Button>
              )}
              {mode === 'EDIT' && allowedActions.isNil.roles.includes(ROLE) && (
                <Button
                  id="nil"
                  type={'submit'}
                  form={'vatReturnForm'}
                  disabled={
                    !allowedActions.isNil.state_status.includes(
                      `${vatReturnDetail.data.vat_return_status_id}${vatReturnDetail.data.vat_return_state}`
                    ) ||
                    [temporaryStatus, finalStatus, nilStatus].includes(
                      StateStatus.Pending
                    )
                  }
                  className={classes.header_actions}
                >
                  <TranslatedText textMap={Translations.action_submit_nil} />
                </Button>
              )}
            </Box>
          )}
        </div>
      </Paper>
      <Box className={classes.errors}>
        {submitError && submitError.length > 0 ? (
          <React.Fragment>
            <p style={{ textDecoration: 'underline' }}>Errors:</p>
            {submitError.map((error, index) => (
              <Box key={index}>{`${index + 1}. ${error}`}</Box>
            ))}
          </React.Fragment>
        ) : null}
      </Box>
      <div className={classes.subtitles}>
        <Typography component={'p'} variant={'h4'}>
          <TranslatedText
            textMap={Translations.subtitle1}
            defaultText={Translations.subtitle1.English}
          />
        </Typography>
        <Typography
          component={'p'}
          variant={'h6'}
          className={classes.subtitle2}
        >
          <TranslatedText
            textMap={Translations.subtitle2}
            defaultText={Translations.subtitle2.English}
          />
        </Typography>
      </div>
      {vatReturnDetail && vatReturnDetail.status === StateStatus.Success && (
        <form
          noValidate
          style={{ width: '100%' }}
          onSubmit={handleSubmit(onSubmit)}
          id="vatReturnForm"
        >
          <GridLayout
            elements={handlePageViews(vatReturnDetail.data.oss_scheme)}
            spacing={3}
          />
        </form>
      )}
      <FormModals />
    </Fragment>
  );
};

const FormModals = (): ReactElement => {
  return (
    <Fragment>
      <BaseModal
        modalID={ModalIDs.MODAL_ADD_SUPPLY_FROM_MEMBER_STATE_OF_IDENTIFICATION}
        renderHeader={() => (
          <TranslatedText textMap={Translations.add_supply} />
        )}
        renderContent={(props) => (
          <AddSupplyFromMemberStateOfIdentificationForm {...props} />
        )}
      />
      <BaseModal
        modalID={ModalIDs.MODAL_ADD_SUPPLY_FROM_FIXED_ESTABLISHMENTS}
        renderHeader={() => (
          <TranslatedText textMap={Translations.add_supply} />
        )}
        renderContent={(props) => (
          <AddSupplyFromFixedEstablishmentsForm {...props} />
        )}
      />
      <BaseModal
        modalID={ModalIDs.MODAL_ADD_VAT_RETURN_CORRECTIONS}
        renderHeader={() => (
          <TranslatedText textMap={Translations.add_correction} />
        )}
        renderContent={(props) => <AddVatReturnCorrectionForm {...props} />}
      />
    </Fragment>
  );
};

const renderIdentificationDetailsForm = (
  classes: any,
  mode: 'VIEW' | 'EDIT',
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <div className={classes.card}>
      <IdentificationDetailsForm
        mode={mode}
        errors={errors}
        control={control}
      />
    </div>
  );
};

const renderVatReturnPeriodDetails = (
  classes: any,
  mode: 'VIEW' | 'EDIT',
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => {
  return (
    <div className={classes.card}>
      <VatReturnPeriodDetails mode={mode} errors={errors} control={control} />
    </div>
  );
};

const renderSuppliesFromMemberStateOfIdentification = (
  classes: any,
  mode: 'VIEW' | 'EDIT',
  vat_number: string,
  // countries: any,
  supplies: any
) => {
  return (
    <div className={classes.card}>
      <SuppliesFromMemberStateOfIdentification
        mode={mode}
        vat_number={vat_number}
        // countries={countries}
        supplies={supplies}
      />
    </div>
  );
};

const renderSuppliesFromFixedEstablishments = (
  classes: any,
  mode: 'VIEW' | 'EDIT',
  vatNumber: string,
  // countries: any,
  vat_return_id: number,
  supplies: any
) => {
  return (
    <div className={classes.card}>
      <SuppliesFromFixedEstablishments
        mode={mode}
        // countries={countries}
        vat_number={vatNumber}
        vat_return_id={vat_return_id}
        supplies={supplies}
      />
    </div>
  );
};

const renderVatAmountTotal = (classes: any, total: number) => {
  return (
    <div className={classes.card}>
      <VatAmountTotal total={total} />
    </div>
  );
};

const renderCorrectionsOfVatReturn = (
  classes: any,
  mode: 'VIEW' | 'EDIT',
  // countries: any,
  vat_return_id: number,
  schemeType: SchemeTypes
) => {
  return (
    <div className={classes.card}>
      <CorrectionsOfVatReturn
        mode={mode}
        // countries={countries}
        vat_return_id={vat_return_id}
        schemeType={schemeType}
      />
    </div>
  );
};

const renderBalanceVatDueForEachMemberState = (classes: any, data: any) => {
  return (
    <div className={classes.card}>
      <BalanceVatDueForEachMemberState data={data} />
    </div>
  );
};

const renderVatReturnTotalAmountDue = (classes: any, data: any) => {
  return (
    <div className={classes.card}>
      <VatReturnTotalAmountDue data={data} />
    </div>
  );
};

export default withRouter(VatReturnDetailPage);
